// src/pages/Reminders.jsx
// Users → Reminders (3.9). One tab per kind of reminder,
// with who is on the list, ticking people to send to, sorting, and each
// person's history in a pop-up:
//   Two-factor not set up   never signed in → the "You're invited" email;
//                           signed in before → a short reminder
//   Inactive people         haven't signed in for N days: a review list to
//                           the account's Admins (and Remove here)
//   Access requests waiting unanswered for 2+ days: to the approvers
//   (individual accounts: the Super Admins). "No account yet" and "Held
//   faxes expiring" were removed in 3.15.0.
// Plus Scheduled reminders and the Sent log, opened from buttons beside
// Refresh. 3.15.0: a schedule per account (on/off, day, time, in the
// account's time zone; "Same as default" otherwise) — Super Admins change
// all of them, people with "Change my account's reminder schedule" their
// own account's.
// Lists update by themselves: people drop off once
// they've done what the reminder asks. Sending by hand can always send
// again; scheduled runs keep a waiting time between reminders.
// Roles & Access: "Reminders" (reminders.view), "Send reminders"
// (reminders.send), "Change my account's reminder schedule" (reminders.schedule).
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from '../components/Notifications';
import { apiCall } from '../components/EmailEditorParts';
import { userAction } from '../utils/api';
import { formatDate } from '../utils/format';
import MultiSearchSelect from '../components/MultiSearchSelect';
import ShowSelect from '../components/ShowSelect';
import { allTimeZones, timeZoneName, timeZone, tzLabel } from '../utils/timeZone';
import { PILL, PILL_ON, TH_SELECT, TH_OPTION } from '../theme/tabs';
import Loader from '../components/Loader';
import Emo, { emoLabel } from '../components/Emo';
import { useClientWord } from '../hooks/useClientWord';

const call = (path, getToken, options) => apiCall(`/portal/reminders${path}`, getToken, options);
const DAY = 24 * 60 * 60 * 1000;
const ago = iso => (iso ? Math.floor((Date.now() - Date.parse(iso)) / DAY) : null);
const LOG_KIND = {
  signin_invite: 'Invite (never signed in)', twofa_invite: 'Invite (set password)', twofa_reminder: 'Two-factor reminder', signup_reminder: 'Account sign-up reminder',
  held_reminder: 'Held faxes expiring', inactive_review: 'Access review (to Admins)', request_reminder: 'Access request waiting (to approvers)',
  unread_reminder: 'Unread faxes (weekly list)',   // 3.49.18
  problems_report: 'Problem numbers report',   // 3.60.3
};
const HOURS = Array.from({ length: 24 }, (_, h) => ({ h, label: `${((h + 11) % 12) + 1}:00 ${h < 12 ? 'AM' : 'PM'}` }));
const hourLabel = h => HOURS[h]?.label || '';

// Each kind: what it is, who is due (the scheduled run's rule), columns
const KINDS = {
  // 3.21.4: people who never signed in get the invite from here (before, from
  // the two-factor list); the schedule stops after 4 (data.signInStop)
  nosignin: {
    label: 'Never signed in', icon: '✉️',
    about: null,   // set from the number of invites
    due: p => !p.stopped && (!p.lastSentAt || Date.now() - Date.parse(p.lastSentAt) >= 3 * DAY),
    wait: 'at most one every 3 days, and stop after 4 invites to the same person',
    cols: [
      { key: 'name', label: 'Person', sort: p => (p.name || p.email).toLowerCase(), cell: p => <Person p={p} /> },
      { key: 'account', label: 'Client', sort: p => p.account || '', cell: p => p.account },
      { key: 'role', label: 'Role', sort: p => p.roleLabel || '', cell: p => p.roleLabel },
      { key: 'added', label: 'Added', sort: p => p.addedAt || '', cell: p => <span style={S.small}>{p.addedAt ? `${formatDate(p.addedAt)} (${ago(p.addedAt)} days)` : '—'}</span> },
      { key: 'status', label: 'Status', sort: p => (p.stopped ? 1 : 0), cell: p => (p.stopped ? <span style={{ ...S.tag, ...S.tagWarn }}>Stopped after {p.timesSent} invites</span> : <span style={S.tag}>Weekly invites</span>) },
    ],
  },
  twofa: {
    label: 'Two-factor not set up', icon: '🔐',
    about: 'People who signed in but haven\'t set up two-factor authentication. They get a short reminder, and drop off once they set it up. (People who never signed in are on the Never signed in list.)',
    due: p => !p.lastSentAt || Date.now() - Date.parse(p.lastSentAt) >= 3 * DAY,
    wait: 'at most one every 3 days',
    cols: [
      { key: 'name', label: 'Person', sort: p => (p.name || p.email).toLowerCase(), cell: p => <Person p={p} /> },
      { key: 'account', label: 'Client', sort: p => p.account || '', cell: p => p.account },
      { key: 'role', label: 'Role', sort: p => p.roleLabel || '', cell: p => p.roleLabel },
      { key: 'last', label: 'Last signed in', sort: p => p.lastActiveAt || '', cell: p => <span style={S.small}>{p.lastActiveAt ? `${formatDate(p.lastActiveAt)} (${ago(p.lastActiveAt)} days)` : '—'}</span> },
    ],
  },
  inactive: {
    label: 'Inactive people', icon: '💤',
    about: null,   // set from the number of days
    due: p => !p.lastSentAt || Date.now() - Date.parse(p.lastSentAt) >= 30 * DAY,
    wait: 'each person at most once a month',
    cols: [
      { key: 'name', label: 'Person', sort: p => (p.name || p.email).toLowerCase(), cell: p => <Person p={p} /> },
      { key: 'account', label: 'Client', sort: p => p.account || '', cell: p => p.account },
      { key: 'role', label: 'Role', sort: p => p.roleLabel || '', cell: p => p.roleLabel },
      { key: 'last', label: 'Last signed in', sort: p => p.lastActiveAt || '', cell: p => <span style={S.small}>{p.lastActiveAt ? `${formatDate(p.lastActiveAt)} (${ago(p.lastActiveAt)} days)` : `Never (added ${p.addedAt ? `${ago(p.addedAt)} days ago` : 'a while ago'})`}</span> },
    ],
  },
  requests: {
    label: 'Access requests waiting', icon: '🙋',
    about: 'People who asked for a Fax Portal account and haven\'t had an answer. The Admins and Managers who can approve them get a reminder once a request has waited 2 days (for an individual client, Fax Portal support). They drop off once approved or declined.',
    due: p => p.due && (!p.lastSentAt || Date.now() - Date.parse(p.lastSentAt) >= 2 * DAY),
    wait: 'each request at most once every 2 days',
    cols: [
      { key: 'name', label: 'Person', sort: p => (p.name || p.email).toLowerCase(), cell: p => <Person p={p} /> },
      { key: 'account', label: 'Client', sort: p => p.account || '', cell: p => p.account },
      { key: 'asked', label: 'Asked on', sort: p => p.requestedAt || '', cell: p => <span style={S.small}>{formatDate(p.requestedAt)} ({ago(p.requestedAt)} days)</span> },
    ],
  },
};
// 'held' and the weekly 'signup' reminder were removed in 3.15.0 (faxes
// aren't held since 3.10.2; refused senders get a notice straight away)
const ORDER = ['nosignin', 'twofa', 'inactive', 'requests'];
// 3.49.18 (owner): Unread faxes, in the schedule only (it has no people list:
// each person's email lists the faxes they can see)
const UNREAD = { label: 'Unread faxes', icon: '📥', wait: 'each person once per run, only when they have unread faxes' };
// 3.60.3 (owner): the Problem numbers report, in the schedule only too
const PROBLEMS = { label: 'Problem numbers report', icon: '⚠️', wait: 'each sender and the people chosen, only when problem numbers were faxed' };
const SCHEDULE_ORDER = [...ORDER, 'unread', 'problems'];
const kindOf = k => KINDS[k] || (k === 'unread' ? UNREAD : k === 'problems' ? PROBLEMS : null);

export default function Reminders() {
  const { getToken } = useAuth();
  const [data, setData]   = useState(null);
  const [error, setError] = useState('');
  const [view, setView]   = useState(() => { try { return window.sessionStorage.getItem('faxPortal.remindersView') || 'nosignin'; } catch { return 'nosignin'; } });
  const [historyOf, setHistoryOf] = useState(null);   // a row

  const load = useCallback(async () => {
    setError('');
    try { setData(await call('', getToken)); } catch (e) { setError(e.message); }
  }, [getToken]);
  useEffect(() => { load(); }, [load]);
  // 3.42.2: Refresh reloads everything on screen (the lists, and the open
  // Scheduled reminders or Sent log, which used to stay as first loaded) and
  // says when it was done
  const [refreshing, setRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshedAt, setRefreshedAt] = useState(null);
  async function refresh() {
    setRefreshing(true);
    await load();
    setRefreshKey(k => k + 1);
    setRefreshedAt(new Date());
    setRefreshing(false);
  }

  const show = v => { setView(v); try { window.sessionStorage.setItem('faxPortal.remindersView', v); } catch { /* ignore */ } };

  if (error) return <div role="alert" style={S.error}>{error}</div>;
  if (!data) return <Loader label="Loading reminders…" />;
  const kinds = ORDER.map(k => ({ id: k, ...KINDS[k], rows: data[k] || [] }));

  return (
    <div>
      <div style={S.top}>
        <div>
          <h3 style={S.title}>Reminders</h3>
          <p style={S.sub}>Who still needs a nudge, and what was sent. Lists update by themselves: people drop off once they've done what the reminder asks. Tick people and send, or use Send again any time.</p>
        </div>
        {/* Settings and the record sit apart from the reminder lists */}
        <div style={S.topActions}>
          <button onClick={() => show(view === 'schedule' ? 'nosignin' : 'schedule')} aria-pressed={view === 'schedule'}
            style={{ ...S.toolBtn, ...(view === 'schedule' ? S.toolBtnOn : {}) }}><Emo e="🗓️" /> Scheduled reminders</button>
          <button onClick={() => show(view === 'log' ? 'nosignin' : 'log')} aria-pressed={view === 'log'}
            style={{ ...S.toolBtn, ...(view === 'log' ? S.toolBtnOn : {}) }}><Emo e="📜" /> Sent log</button>
          <button onClick={refresh} disabled={refreshing} style={S.refreshBtn} title="Load the lists, the schedule and the sent log again">{refreshing ? 'Refreshing…' : emoLabel('↻ Refresh')}</button>
          {refreshedAt && !refreshing && <span style={S.refreshed}>Updated {refreshedAt.toLocaleTimeString('en-US', { timeZone: timeZone(), hour: 'numeric', minute: '2-digit', second: '2-digit' })} {tzLabel(refreshedAt)}</span>}
        </div>
      </div>

      <div role="tablist" aria-label="Reminder lists" style={S.views}>
        {kinds.map(k => {
          const due = k.rows.filter(p => p.canSend && k.due(p)).length;
          return (
            <button key={k.id} role="tab" aria-selected={view === k.id} onClick={() => show(k.id)} style={{ ...S.view, ...(view === k.id ? S.viewOn : {}), ...(!KINDS[view] ? S.viewDim : {}) }}>
              <Emo e={k.icon} /> {k.label} <span style={S.count}>{k.rows.length}</span>
              {due > 0 && <span style={S.dueDot} title={`${due} due`}>{due} due</span>}
            </button>
          );
        })}
      </div>
      {!KINDS[view] && (
        <div style={S.backRow}>
          <button onClick={() => show('nosignin')} style={S.linkBtn}>← Back to the reminder lists</button>
          <span style={S.panelTitle}>{view === 'schedule' ? <><Emo e="🗓️" /> Scheduled reminders</> : <><Emo e="📜" /> Sent log</>}</span>
        </div>
      )}

      {KINDS[view] && (
        <KindPanel key={view} kind={view} def={KINDS[view]} rows={data[view] || []} data={data} getToken={getToken}
          onChanged={load} onHistory={setHistoryOf} />
      )}
      {view === 'schedule' && <Schedule key={refreshKey} data={data} getToken={getToken} onChanged={load} />}
      {view === 'log' && <SentLog key={refreshKey} getToken={getToken} />}
      {historyOf && <History row={historyOf} getToken={getToken} onClose={() => setHistoryOf(null)} />}
    </div>
  );
}

function Person({ p }) {
  return <><div style={S.name}>{p.name || p.email}</div>{p.name && <div style={S.meta}>{p.email}</div>}</>;
}

// Sort rows by a column: [rows, sortProps(col) for the header]
function useSorted(rows, cols, initial) {
  const [sort, setSort] = useState(initial);
  const sorted = useMemo(() => {
    const col = cols.find(c => c.key === sort.key);
    if (!col) return rows;
    const val = r => (typeof col.sort === 'function' ? col.sort(r) : r[col.key]) ?? '';
    return [...rows].sort((a, b) => {
      const x = val(a), y = val(b);
      const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
      return sort.dir === 'asc' ? c : -c;
    });
  }, [rows, cols, sort]);
  const header = col => ({
    onClick: () => setSort(s => ({ key: col.key, dir: s.key === col.key && s.dir === 'asc' ? 'desc' : 'asc' })),
    'aria-sort': sort.key === col.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none',
    arrow: sort.key === col.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '',
  });
  return [sorted, header];
}

// How often a reminder on a weekday runs (3.21.5)
const EVERY = [{ v: 'week', label: 'Every week' }, { v: '2weeks', label: 'Every 2 weeks' }, { v: 'month', label: 'Once a month' }];
const whenText = s => (s.day === 'daily' ? 'every day'
  : s.every === '2weeks' ? `every other ${s.day}` : s.every === 'month' ? `on the first ${s.day} of each month` : `every ${s.day}`);
function scheduleText(kind, s, days, tz = 'America/New_York', accountName = null) {
  if (!s) return '';
  const whose = accountName ? `${accountName}: ` : '';
  if (!s.on) return `${whose}scheduled sending is off.`;
  return `${whose}sent by itself ${whenText(s)} at ${hourLabel(s.hour)} ${timeZoneName(tz)}${kind === 'inactive' ? ` (people inactive for ${days}+ days)` : ''}.`;
}
// The schedule a list shows: the account picked at the top, else the default
function shownSchedule(data, kind) {
  const acc = (data.accounts || []).find(a => a.id === data.accountId);
  return acc ? { s: acc.schedule[kind], tz: acc.timeZone, name: acc.name, days: acc.inactiveDays } : { s: data.settings.schedule[kind], tz: data.defaultTimeZone, name: null, days: data.settings.inactiveDays };
}

function KindPanel({ kind, def, rows: allRows, data, getToken, onChanged, onHistory }) {
  const clientWord = useClientWord();   // 3.67.5: 'Company' for the people at a client
  const { notify, confirm } = useNotify();
  // Search (3.18.3): suggests names as you type (3.18.4: names only; the
  // Account column has its own picker), several can be picked; Show: 50
  const [terms, setTerms] = useState([]);
  const [showN, setShowN] = useState('50');
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState('');
  const cols = useMemo(() => [
    ...def.cols,
    { key: 'lastSentAt', label: kind === 'inactive' ? 'Last reviewed' : 'Last sent', sort: p => p.lastSentAt || '', cell: p => <span style={S.small}>{p.lastSentAt ? formatDate(p.lastSentAt) : '—'}</span> },
    { key: 'timesSent', label: 'Times', sort: p => p.timesSent || 0, cell: p => p.timesSent || 0 },
  ], [def, kind]);
  // Account picker in the Account column (3.18.1), when the list has more
  // than one account (Super Admins on All accounts)
  const [accountPick, setAccountPick] = useState('');
  // 3.18.2: every account (Super Admins on All accounts), not only those on
  // the list now, so the picker is always there
  const { effectiveInfo } = useAuth();
  const allAccountsView = effectiveInfo?.level === 'super_admin' && (!effectiveInfo?.accountId || effectiveInfo.accountId === 'all');
  const accountOptions = useMemo(() => (allAccountsView
    ? [...new Set([...(effectiveInfo.accounts || []).map(a => a.name), ...allRows.map(p => p.account)].filter(Boolean))].sort()
    : []), [allAccountsView, effectiveInfo, allRows]);
  const rows = useMemo(() => (accountPick ? allRows.filter(p => p.account === accountPick) : allRows), [allRows, accountPick]);
  const [sortedAll, header] = useSorted(rows, cols, { key: cols[0].key, dir: 'asc' });
  const nameOf = p => p.name || p.email;   // people without a name show their email
  const termOptions = useMemo(() => [...new Set(rows.map(nameOf).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [rows]);
  const matching = useMemo(() => {
    const ts = terms.map(t => String(t).trim().toLowerCase()).filter(Boolean);
    return !ts.length ? sortedAll : sortedAll.filter(p => ts.some(t => String(nameOf(p) || '').toLowerCase().includes(t)));
  }, [sortedAll, terms]);
  const shown = showN === 'all' ? matching : matching.slice(0, Number(showN) || 50);
  const canPick = p => data.canSend && p.canSend;
  const pickable = shown.filter(canPick);
  const allPicked = pickable.length > 0 && pickable.every(p => picked.has(p.email));
  const toggle = email => setPicked(s => { const n = new Set(s); if (n.has(email)) n.delete(email); else n.add(email); return n; });
  const dueRows = rows.filter(p => canPick(p) && def.due(p));
  const about = kind === 'inactive'
    ? `People who haven't signed in for ${shownSchedule(data, 'inactive').days} days or more${(data.accounts || []).some(a => a.inactiveDaysCustom) && !data.accountId ? ' (or their client\'s own number of days)' : ''} (or never, if added that long ago). Their client's Admins get a list to review, and you can remove access here. They drop off once they sign in or are removed.`
    : kind === 'nosignin'
      ? `People who were added but have never signed in. They get the "You're invited" email with a link to set their password. Scheduled runs stop after ${data.signInStop || 4} invites to the same person; you can still send one by hand. They drop off once they sign in or are removed.`
      : def.about;

  async function send(list) {
    if (!list.length) return;
    const recent = list.filter(p => !def.due(p)).length;
    const toAdmins = kind === 'inactive' || kind === 'requests';
    const ok = await confirm({
      title: toAdmins
        ? `Remind the Admins about ${list.length === 1 ? (list[0].name || list[0].email) : `${list.length} people`}?`
        : list.length === 1 ? `Email ${list[0].name || list[0].email}?` : `Email ${list.length} people?`,
      message: [
        kind === 'nosignin' && 'They get the "You\'re invited" email with a link to set their password (valid 72 hours). It replaces any password they have now.',
        kind === 'twofa' && 'They get a reminder to set up two-factor authentication.',
        kind === 'signup' && 'They get an email asking them to get a Fax Portal account, with a Request access button.',
        kind === 'held' && 'Each sender gets a reminder that their faxes are on hold, and their company\'s Admins get one summary.',
        kind === 'inactive' && 'Their company\'s Admins get one email listing them, asking whether they still need access.',
        kind === 'requests' && 'The Admins and Managers who can approve them get one email listing the requests.',
        recent ? `${recent} of them had one recently; this sends again.` : '',
      ].filter(Boolean).join(' '),
      confirmLabel: 'Send',
    });
    if (!ok) return;
    setBusy('send');
    try {
      const r = await call('/send', getToken, { method: 'POST', body: JSON.stringify({ kind, emails: list.map(p => p.email), force: true }) });
      const n = r.sent || 0;
      notify({ tone: r.failed ? 'error' : 'success', title: toAdmins ? `Admins reminded about ${n} ${n === 1 ? 'person' : 'people'}` : `${n} email${n === 1 ? '' : 's'} sent`, detail: r.failed ? `${r.failed} couldn't be sent. Try again in a few minutes.` : undefined });
      setPicked(new Set());
      await onChanged();
    } catch (e) { notify({ tone: 'error', title: "Couldn't send", detail: e.message }); }
    setBusy('');
  }

  async function remove(p) {
    const ok = await confirm({ title: `Remove ${p.name || p.email}?`, message: 'They can no longer sign in to the Fax Portal. You can give access back later in People.', confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    setBusy(`remove:${p.email}`);
    try { await userAction(p.email, 'remove', getToken); notify({ tone: 'success', title: `${p.name || p.email} removed` }); await onChanged(); }
    catch (e) { notify({ tone: 'error', title: "Couldn't remove", detail: e.message }); }
    setBusy('');
  }

  const selected = rows.filter(p => picked.has(p.email) && canPick(p));
  return (
    <section style={S.card} aria-label={def.label}>
      <p style={S.note}>{about}</p>
      <p style={S.schedLine}><Emo e="🗓️" /> {(() => { const x = shownSchedule(data, kind); return scheduleText(kind, x.s, x.days, x.tz, x.name); })()} Scheduled runs send {def.wait}.</p>
      <div style={S.toolbar}>
        <div style={S.searchRow}>
          <div style={{ minWidth: 260 }}>
            <MultiSearchSelect values={terms} onChange={setTerms} options={termOptions} placeholder="Search name" />
          </div>
          <ShowSelect value={showN} onChange={setShowN} groupStyle={S.showGroup} labelStyle={S.showLabel} style={S.select} />
          <span style={S.meta}>{shown.length < matching.length ? `${shown.length} of ${matching.length}` : `${matching.length} ${matching.length === 1 ? 'person' : 'people'}`}</span>
        </div>
        {data.canSend && (
          <div style={S.cardActions}>
            {dueRows.length > 0 && <button onClick={() => setPicked(new Set(dueRows.map(p => p.email)))} style={S.actionBtn}>Tick everyone due ({dueRows.length})</button>}
            <button onClick={() => send(selected)} disabled={!selected.length || !!busy} style={{ ...S.primaryBtn, opacity: !selected.length || busy ? 0.5 : 1 }}>
              {busy === 'send' ? 'Sending…' : <><Emo e="📨" /> Send to ticked ({selected.length})</>}
            </button>
          </div>
        )}
      </div>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr style={S.thead}>
              {data.canSend && (
                <th style={{ ...S.th, width: 34 }}>
                  <input type="checkbox" aria-label="Tick everyone shown" checked={allPicked} disabled={!pickable.length}
                    onChange={() => setPicked(s => { const n = new Set(s); pickable.forEach(p => (allPicked ? n.delete(p.email) : n.add(p.email))); return n; })} />
                </th>
              )}
              {cols.map(c => { const h = header(c); return c.key === 'account' && accountOptions.length > 0 ? (
                <th key={c.key} style={S.th}>
                  <select value={accountPick} onChange={e => { setAccountPick(e.target.value); setPicked(new Set()); }} aria-label={clientWord} style={S.thSelect}>
                    <option value="" style={S.thOption}>{clientWord}: All</option>
                    {accountOptions.map(a => <option key={a} value={a} style={S.thOption}>{a}</option>)}
                  </select>
                </th>
              ) : (
                <th key={c.key} style={{ ...S.th, cursor: 'pointer' }} aria-sort={h['aria-sort']}>
                  <button onClick={h.onClick} style={S.sortBtn}>{c.key === 'account' ? clientWord : c.label}{h.arrow}</button>
                </th>
              ); })}
              <th style={S.th}><span style={S.srOnly}>Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {!shown.length ? <tr><td colSpan={cols.length + 2} style={S.empty}>{rows.length ? 'Nobody matches your search.' : <>Nobody on this list right now. <Emo e="🎉" /></>}</td></tr>
              : shown.map((p, i) => {
                const isDue = def.due(p);
                return (
                  <tr key={p.email} style={{ background: picked.has(p.email) ? 'var(--c-e0f2f1)' : i % 2 ? 'var(--c-ffffff)' : 'var(--c-fafafa)', borderBottom: '1px solid var(--c-f0f0f0)' }}>
                    {data.canSend && <td style={S.td}><input type="checkbox" aria-label={`Tick ${p.name || p.email}`} checked={picked.has(p.email)} disabled={!canPick(p)} onChange={() => toggle(p.email)} /></td>}
                    {cols.map(c => <td key={c.key} style={S.td}>{c.cell(p)}</td>)}
                    <td style={{ ...S.td, textAlign: 'right' }}>
                      <div style={S.actions}>
                        <button onClick={() => onHistory({ ...p, kind })} style={S.linkBtn}>History</button>
                        {canPick(p) && <button onClick={() => send([p])} disabled={!!busy} style={S.actionBtn}>{isDue ? 'Send' : 'Send again'}</button>}
                        {kind === 'inactive' && p.canRemove && <button onClick={() => remove(p)} disabled={!!busy} style={S.dangerBtn}>{busy === `remove:${p.email}` ? 'Removing…' : 'Remove'}</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// Scheduled reminders (3.15.0): the default schedule and each account's own,
// in the account's time zone. Super Admins change all of them; people with
// "Change my account's reminder schedule" their own account's.
function Schedule({ data, getToken, onChanged }) {
  const { notify } = useNotify();
  const [days, setDays] = useState(String(data.settings.inactiveDays));
  const accounts = data.accounts || [];
  const [which, setWhich] = useState(() => (data.canEditSchedule ? (data.accountId || '') : (accounts[0]?.id || '')));
  const account = accounts.find(a => a.id === which) || null;
  const shownDays = account ? account.inactiveDays : data.settings.inactiveDays;
  useEffect(() => { setDays(String(shownDays)); }, [shownDays, which]);
  const edit = account ? account.canEdit : data.canEditSchedule;
  const tz = account ? account.timeZone : data.defaultTimeZone;
  async function put(path, body, title = 'Saved') {
    try { await call(path, getToken, { method: 'PUT', body: JSON.stringify(body) }); await onChanged(); notify({ tone: 'success', title }); }
    catch (e) { notify({ tone: 'error', title: "Couldn't save", detail: e.message }); }
  }
  const save = (kind, change) => put('/schedule', { kind, ...change, ...(account ? { accountId: account.id } : {}) });
  const zones = allTimeZones();
  // 3.21.6: who gets the Admin reminders (inactive, requests) for this client
  const [recips, setRecips] = useState(null);
  const [choosing, setChoosing] = useState(null);   // the kind whose recipients are being picked
  const loadRecips = useCallback(async () => {
    setRecips(null);
    if (!account) return;
    try { setRecips(await call(`/recipients?accountId=${encodeURIComponent(account.id)}`, getToken)); } catch { setRecips(null); }
  }, [account, getToken]);
  useEffect(() => { loadRecips(); }, [loadRecips]);
  return (
    <section style={S.card} aria-label="Scheduled reminders">
      <div style={S.cardActions}>
        <label style={S.meta}>Schedule for{' '}
          <select value={which} onChange={e => setWhich(e.target.value)} style={S.select} aria-label="Whose schedule">
            {data.canEditSchedule && <option value="">Default (clients that don't have their own)</option>}
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </label>
        {account && (
          <label style={S.meta}>Time zone{' '}
            <select value={account.timeZone} disabled={!edit} onChange={e => put('/timezone', { accountId: account.id, timeZone: e.target.value })} style={S.select} aria-label="Time zone">
              {(zones.includes(account.timeZone) ? zones : [account.timeZone, ...zones]).map(z => <option key={z} value={z}>{timeZoneName(z)}</option>)}
            </select>
          </label>
        )}
      </div>
      <p style={S.note}>
        {account
          ? <>Reminders the portal sends by itself for <strong>{account.name}</strong>, to its people only. Times are in its time zone ({timeZoneName(tz)}). A reminder that isn't set for it follows the default schedule.</>
          : <>The default schedule, for every client that doesn't have its own. Times are {timeZoneName(tz)} unless a client picks its own time zone.</>}
        {' '}Each kind keeps its waiting time between reminders, so nobody gets them too often.{!edit && ' You can see this schedule but not change it.'}
      </p>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr style={S.thead}><th style={S.th}>Reminder</th>{account && <th style={S.th}>Schedule</th>}<th style={S.th}>On</th><th style={S.th}>Day</th><th style={S.th}>How often</th><th style={S.th}>Time</th></tr></thead>
          <tbody>
            {SCHEDULE_ORDER.filter(k => (account ? account.schedule[k] : data.settings.schedule[k])).map((k, i) => {
              const s = account ? account.schedule[k] : data.settings.schedule[k];
              const own = !account || s.custom;
              return (
                <Fragment key={k}>
                <tr style={{ background: i % 2 ? 'var(--c-ffffff)' : 'var(--c-fafafa)', borderBottom: '1px solid var(--c-f0f0f0)' }}>
                  <td style={S.td}>
                    <div style={S.name}><Emo e={kindOf(k).icon} /> {kindOf(k).label}</div><div style={S.meta}>{kindOf(k).wait}</div>
                    {k === 'unread' && <div style={S.meta}>Received faxes from the past week that the person hasn’t opened yet. Each person whose role includes it (Roles &amp; Access → Inbox) gets the ones they can see, with an Excel list.</div>}
                    {k === 'problems' && <div style={S.meta}>Fax numbers on Logs → Problem numbers that people faxed in the period: each sender gets their own, and the people chosen below get everyone’s. The period follows how often it comes: the last 7 days (every week), 14 days (every 2 weeks) or 30 days (every month).</div>}
                    {/* 3.42.2: how long counts as inactive sits with the reminder (it was squeezed in by the time) */}
                    {k === 'inactive' && (
                      <div style={S.inactiveRow}>
                        Inactive after <input type="number" min="7" max="365" value={days} disabled={!edit} onChange={e => setDays(e.target.value)} aria-label="Days without signing in" style={{ ...S.select, width: 72 }} /> days without signing in
                        {edit && String(account ? account.inactiveDays : data.settings.inactiveDays) !== days && <button onClick={() => put('/settings', { inactiveDays: Number(days), ...(account ? { accountId: account.id } : {}) })} style={S.linkBtn}>Save</button>}
                        {/* 3.21.7: a client's own number, or the default */}
                        {account && (account.inactiveDaysCustom
                          ? edit && <> · <button onClick={() => put('/settings', { accountId: account.id, useDefault: true })} style={S.linkBtn}>Use the default ({data.settings.inactiveDays})</button></>
                          : <> (the default)</>)}
                      </div>
                    )}
                    {/* 3.68.6 (owner): the Problem numbers report gets a picker too */}
                    {(k === 'inactive' || k === 'requests' || k === 'problems') && (account
                      ? recips?.[k] && <SentTo kind={k} r={recips[k]} canEdit={recips.canEdit && choosing !== k} onChoose={() => setChoosing(k)} />
                      : <div style={S.meta}>Sent to each client's {whoOf(k)} (pick a client above to choose who).</div>)}
                  </td>
                  {account && (
                    <td style={S.td}>
                      <label style={S.switchRow}>
                        <input type="checkbox" checked={!s.custom} disabled={!edit}
                          onChange={e => (e.target.checked ? save(k, { useDefault: true }) : save(k, { on: s.on, day: s.day, hour: s.hour, every: s.every || 'week' }))} /> Same as default
                      </label>
                    </td>
                  )}
                  <td style={S.td}>
                    <label style={{ ...S.switchRow, whiteSpace: 'nowrap' }}>
                      <input type="checkbox" checked={!!s.on} disabled={!edit || !own} onChange={e => save(k, { on: e.target.checked })} /> {s.on ? 'On' : 'Off'}
                    </label>
                  </td>
                  <td style={S.td}>
                    <select value={s.day} disabled={!edit || !own} onChange={e => save(k, { day: e.target.value })} aria-label={`${kindOf(k).label}: day`} style={{ ...S.select, width: 150 }}>
                      {data.days.map(d => <option key={d} value={d}>{d === 'daily' ? 'Every day' : d}</option>)}
                    </select>
                  </td>
                  <td style={S.td}>
                    {s.day === 'daily' ? <span style={{ ...S.meta, display: 'inline-block', paddingTop: 7 }}>Every day</span> : (
                      <select value={s.every || 'week'} disabled={!edit || !own} onChange={e => save(k, { every: e.target.value })} aria-label={`${kindOf(k).label}: how often`} style={{ ...S.select, width: 250 }}>
                        {EVERY.map(x => <option key={x.v} value={x.v}>{x.v === 'month' ? `First ${s.day} of the month` : x.label}</option>)}
                      </select>
                    )}
                  </td>
                  <td style={S.td}>
                    <select value={s.hour} disabled={!edit || !own} onChange={e => save(k, { hour: Number(e.target.value) })} aria-label={`${kindOf(k).label}: time`} style={{ ...S.select, width: 120 }}>
                      {HOURS.map(h => <option key={h.h} value={h.h}>{h.label}</option>)}
                    </select>
                  </td>
                </tr>
                {choosing === k && account && recips?.[k] && (
                  <tr><td colSpan={account ? 6 : 5} style={S.td}>
                    <PickRecipients kind={k} r={recips[k]} onCancel={() => setChoosing(null)}
                      onSave={async v => { await put('/recipients', { accountId: account.id, kind: k, ...v }, 'Recipients saved'); setChoosing(null); await loadRecips(); }} />
                  </td></tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// Who gets an Admin reminder for one client (3.21.6): the default people, or
// the people ticked; and whether the Super Admins are copied. 3.68.6 (owner):
// Admins, Master Admins and Managers can be ticked (a Manager in a team gets
// only their teams' people); only Admins and Super Admins choose.
const whoOf = kind => (kind === 'requests' ? 'Admins and Managers who can approve' : 'Admins');
function SentTo({ kind, r, canEdit, onChoose }) {
  const chosen = r.eligible.filter(x => (r.picked || []).includes(x.email));
  const byDefault = r.eligible.filter(x => x.byDefault);
  return (
    <div style={S.meta}>
      Sent to: <strong>{chosen.length ? chosen.map(x => x.name).join(', ') : `all ${byDefault.length} ${whoOf(kind)}`}</strong>{r.copySuper ? ' · Fax Portal support copied' : ''}
      {canEdit && <> · <button onClick={onChoose} style={S.linkBtn}>Choose…</button></>}
    </div>
  );
}
function PickRecipients({ kind, r, onSave, onCancel }) {
  const [picked, setPicked] = useState(() => new Set(r.picked || []));
  const [copySuper, setCopySuper] = useState(r.copySuper);
  return (
    <div style={S.sentTo}>
      <div style={S.name}>Who gets {kind === 'problems' ? 'the company’s Problem numbers report' : `${KINDS[kind].label.toLowerCase()} reminders`}? <span style={S.meta}>(none ticked = all {whoOf(kind)})</span></div>
      {r.eligible.map(x => (
        <label key={x.email} style={S.pickRow}>
          <input type="checkbox" checked={picked.has(x.email)} onChange={() => setPicked(s => { const n = new Set(s); if (n.has(x.email)) n.delete(x.email); else n.add(x.email); return n; })} />
          <span>{x.name} <span style={S.meta}>· {x.roleLabel}{x.teamOnly ? ' (their teams only)' : ''}{x.name !== x.email ? ` · ${x.email}` : ''}</span></span>
        </label>
      ))}
      <label style={{ ...S.pickRow, marginTop: 6 }}>
        <input type="checkbox" checked={copySuper} onChange={e => setCopySuper(e.target.checked)} /> <span>Also copy Fax Portal support</span>
      </label>
      <div style={{ ...S.cardActions, marginTop: 6 }}>
        <button onClick={() => onSave({ picked: [...picked], copySuper })} style={S.actionBtn}>Save</button>
        <button onClick={onCancel} style={S.linkBtn}>Cancel</button>
      </div>
    </div>
  );
}

function SentLog({ getToken }) {
  const clientWord = useClientWord();   // 3.67.5: 'Company' for the people at a client
  const [log, setLog] = useState(null);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('');
  const [showN, setShowN] = useState('50');   // Show (3.18.3)
  const cols = useMemo(() => [
    { key: 'at', label: 'When', sort: r => r.at, cell: r => <span style={S.small}>{formatDate(r.at)}</span> },
    { key: 'email', label: 'About', sort: r => r.email, cell: r => r.email },
    { key: 'kind', label: 'Email', sort: r => LOG_KIND[r.kind] || r.kind, cell: r => LOG_KIND[r.kind] || r.kind },
    { key: 'to', label: 'Sent to', sort: r => (r.to || [r.email]).join(', '), cell: r => <span style={S.meta}>{(r.to || [r.email]).join(', ')}</span> },
    { key: 'by', label: 'How', sort: r => (r.trigger === 'weekly' ? '' : r.by), cell: r => (r.trigger === 'weekly' ? <span style={S.tag}>Scheduled</span> : <span style={S.meta}>Sent by {r.by}</span>) },
  ], []);
  useEffect(() => { call('/log', getToken).then(r => setLog(r.data || [])).catch(() => setLog([])); }, [getToken]);
  const [sorted, header] = useSorted(log || [], cols, { key: 'at', dir: 'desc' });
  const matching = sorted.filter(r => (!kind || r.kind === kind) && (!q.trim() || [r.email, ...(r.to || []), r.by].some(v => String(v || '').toLowerCase().includes(q.trim().toLowerCase()))));
  const shown = showN === 'all' ? matching : matching.slice(0, Number(showN) || 50);
  if (!log) return <Loader label="Loading the sent log…" />;
  return (
    <section style={S.card} aria-label="Sent log">
      <div style={S.toolbar}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search email or sender" aria-label="Search the sent log" style={S.search} />
        <select value={kind} onChange={e => setKind(e.target.value)} aria-label="Kind of email" style={S.select}>
          <option value="">Every kind</option>
          {Object.entries(LOG_KIND).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <ShowSelect value={showN} onChange={setShowN} groupStyle={S.showGroup} labelStyle={S.showLabel} style={S.select} />
        <span style={S.meta}>{shown.length} of {matching.length < log.length ? `${matching.length} matching (${log.length} in all)` : log.length}</span>
      </div>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr style={S.thead}>{cols.map(c => { const h = header(c); return <th key={c.key} style={S.th} aria-sort={h['aria-sort']}><button onClick={h.onClick} style={S.sortBtn}>{c.key === 'account' ? clientWord : c.label}{h.arrow}</button></th>; })}</tr></thead>
          <tbody>
            {!shown.length ? <tr><td colSpan={cols.length} style={S.empty}>{log.length ? 'Nothing matches.' : 'No reminders sent yet.'}</td></tr>
              : shown.map((r, i) => <tr key={`${r.at}${r.email}${i}`} style={{ background: i % 2 ? 'var(--c-ffffff)' : 'var(--c-fafafa)', borderBottom: '1px solid var(--c-f0f0f0)' }}>{cols.map(c => <td key={c.key} style={S.td}>{c.cell(r)}</td>)}</tr>)}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// One person's reminders, in a pop-up next to the list
function History({ row, getToken, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => { call(`/log?email=${encodeURIComponent(row.email)}`, getToken).then(r => setItems(r.data || [])).catch(() => setItems([])); }, [row.email, getToken]);
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div style={S.overlay} onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={`Reminders for ${row.email}`} style={S.dialog} onClick={e => e.stopPropagation()}>
        <div style={S.dialogHead} className="dlg-head">
          <div><div style={S.name}>{row.name || row.email}</div>{row.name && <div style={S.meta}>{row.email}</div>}</div>
          <button onClick={onClose} aria-label="Close" style={S.closeBtn} autoFocus>✕</button>
        </div>
        {!items ? <Loader size="sm" />
          : !items.length ? <div style={S.muted}>Nothing sent about this person yet.</div>
          : (
            <ol style={S.timeline}>
              {items.map((r, i) => (
                <li key={i} style={S.timelineItem}>
                  <div style={S.name}>{LOG_KIND[r.kind] || r.kind}</div>
                  <div style={S.meta}>{formatDate(r.at)} · {r.trigger === 'weekly' ? 'Scheduled' : `Sent by ${r.by}`}</div>
                  {r.to && <div style={S.meta}>To {r.to.join(', ')}</div>}
                </li>
              ))}
            </ol>
          )}
      </div>
    </div>
  );
}

const btn = { padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' };
const S = {
  pickRow: { display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--t-37474f)', overflowWrap: 'anywhere' },
  sentTo: { marginTop: 6, padding: '8px 10px', background: 'var(--c-f5faf9)', border: '1px solid var(--c-e0f2f1)', borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 3 },
  top: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 12 },
  title: { margin: '0 0 4px', color: 'var(--t-00695c)', fontSize: 18, fontWeight: 700 },
  sub: { margin: 0, color: 'var(--t-607d8b)', fontSize: 13, maxWidth: 720, lineHeight: 1.5 },
  muted: { color: 'var(--t-90a4ae)', fontSize: 14, padding: 16 },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '10px 14px', borderRadius: 8, fontSize: 13 },
  refreshBtn: { ...btn, padding: '8px 16px', background: 'var(--c-37474f)', color: 'var(--t-ffffff)', border: 'none', fontSize: 13 },
  topActions: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  toolBtn: { ...btn, padding: '8px 14px', background: 'var(--c-ffffff)', color: 'var(--t-37474f)', border: '1.5px solid var(--c-b0bec5)', fontSize: 13, borderRadius: 8 },
  toolBtnOn: { background: 'var(--c-37474f)', border: '1.5px solid var(--c-37474f)', color: 'var(--t-ffffff)' },
  viewDim: { opacity: 0.75 },
  backRow: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '10px 0 0' },
  panelTitle: { fontSize: 15, fontWeight: 700, color: 'var(--t-263238)' },
  views: { display: 'flex', gap: 6, flexWrap: 'wrap', margin: '6px 0 4px' },
  view: { ...PILL, display: 'inline-flex', alignItems: 'center', gap: 6 },
  viewOn: PILL_ON,
  count: { fontSize: 11, fontWeight: 700, background: 'rgb(var(--c-000000-rgb) / 0.08)', borderRadius: 10, padding: '1px 7px' },
  dueDot: { fontSize: 10, fontWeight: 700, background: 'var(--c-fff3e0)', color: 'var(--t-e65100)', borderRadius: 10, padding: '1px 7px' },
  card: { background: 'var(--c-ffffff)', borderRadius: 12, padding: '16px 18px', margin: '14px 0', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)' },
  cardActions: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  searchRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  showGroup: { display: 'flex', alignItems: 'center', gap: 6 },
  showLabel: { fontSize: 13, color: 'var(--t-607d8b)', fontWeight: 600 },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', margin: '8px 0 10px' },
  switchRow: { display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: 'var(--t-37474f)', cursor: 'pointer' },
  inactiveRow: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 8, fontSize: 12.5, color: 'var(--t-546e7a)' },   // 3.42.2
  refreshed: { fontSize: 12, color: 'var(--t-78909c)', alignSelf: 'center' },   // 3.42.2
  note: { margin: '0 0 6px', fontSize: 13, color: 'var(--t-607d8b)', lineHeight: 1.5 },
  schedLine: { margin: '0 0 6px', fontSize: 12, color: 'var(--t-78909c)' },
  primaryBtn: { ...btn, padding: '8px 14px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', fontSize: 13 },
  actionBtn: { ...btn, background: 'var(--c-ffffff)', color: 'var(--t-00695c)', border: '1.5px solid var(--c-80cbc4)' },
  dangerBtn: { ...btn, background: 'var(--c-ffffff)', color: 'var(--t-c62828)', border: '1.5px solid var(--c-ef9a9a)' },
  linkBtn: { background: 'none', border: 'none', color: 'var(--t-00897b)', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', padding: '6px 4px' },
  thSelect: TH_SELECT,
  thOption: TH_OPTION,
  sortBtn: { background: 'none', border: 'none', color: 'var(--t-ffffff)', font: 'inherit', cursor: 'pointer', padding: 0, textTransform: 'inherit', letterSpacing: 'inherit' },
  actions: { display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' },
  tableWrap: { overflow: 'auto', borderRadius: 10, border: '1px solid var(--c-eceff1)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'var(--c-00695c)' },
  th: { padding: '9px 12px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' },
  td: { padding: '8px 12px', fontSize: 13, color: 'var(--t-333333)', verticalAlign: 'top', overflowWrap: 'anywhere' },
  empty: { textAlign: 'center', padding: '24px 0', color: 'var(--t-90a4ae)', fontSize: 13 },
  name: { fontWeight: 600, color: 'var(--t-263238)' },
  meta: { fontSize: 12, color: 'var(--t-78909c)' },
  small: { fontSize: 12, whiteSpace: 'nowrap' },
  tag: { display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 12, background: 'var(--c-e0f2f1)', color: 'var(--t-00695c)', whiteSpace: 'nowrap' },
  tagWarn: { background: 'var(--c-fff3e0)', color: 'var(--t-e65100)' },
  tagOff: { background: 'var(--c-eceff1)', color: 'var(--t-546e7a)' },
  search: { padding: '7px 12px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 13, minWidth: 220, flex: '1 1 220px', maxWidth: 360, fontFamily: 'inherit' },
  select: { padding: '6px 8px', borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', fontSize: 13, fontFamily: 'inherit', background: 'var(--c-ffffff)' },
  srOnly: { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' },
  overlay: { position: 'fixed', inset: 0, background: 'rgb(var(--c-263238-rgb) / 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
  dialog: { background: 'var(--c-ffffff)', borderRadius: 12, padding: '16px 18px', width: '100%', maxWidth: 480, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 30px rgb(var(--c-000000-rgb) / 0.2)' },
  dialogHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10 },
  closeBtn: { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--t-546e7a)', padding: 4 },
  timeline: { listStyle: 'none', margin: 0, padding: 0 },
  timelineItem: { padding: '8px 0 8px 12px', borderLeft: '3px solid var(--c-80cbc4)', marginBottom: 8 },
};
